import { useCallback, useEffect, useRef, useState } from "react";
import {
  completeDocuSignAcknowledgment,
  createDocuSignEnvelope,
  fetchDocuSignEnvelopeStatus,
  simulateDocuSignCompletion
} from "../api/client";
import { instructionVersionLabel } from "../domain/buyerFundingAcknowledgment";
import type { BuyerFundingFlowDocuSignAck, BuyerFundingFlowSession } from "../flows/buyerFunding/types";

type Props = {
  session: BuyerFundingFlowSession;
  onSigned: (ack: BuyerFundingFlowDocuSignAck) => void;
};

function demoRecordingMode(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem("buyer-funding-demo-record") === "1";
}

export function DocuSignAckPanel({ session, onSigned }: Props) {
  const [envelopeId, setEnvelopeId] = useState<string | undefined>(session.docuSignAck?.envelopeId);
  const [signingUrl, setSigningUrl] = useState<string | undefined>();
  const [mode, setMode] = useState<"live" | "simulated" | undefined>(session.docuSignAck?.mode);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [limitation, setLimitation] = useState(session.docuSignAck?.limitation || "");
  const pollRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const versionLabel = instructionVersionLabel(session.fileCode, session.instructions.versionLabel);
  const viewedAt = session.instructionsDownloadedAt || new Date().toISOString();

  const finishAck = useCallback(
    async (id: string, envelopeMode: "live" | "simulated") => {
      setBusy(true);
      setError("");
      try {
        const ack = await completeDocuSignAcknowledgment({
          fileCode: session.fileCode,
          envelopeId: id,
          mode: envelopeMode,
          instructionVersion: session.instructions.versionLabel,
          instructionsViewedAt: viewedAt
        });
        onSigned(ack);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : "Could not record DocuSign acknowledgment.");
      } finally {
        setBusy(false);
      }
    },
    [onSigned, session.fileCode, session.instructions.versionLabel, viewedAt]
  );

  useEffect(() => {
    if (session.docuSignAck) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("esign") === "complete" && envelopeId) {
      void finishAck(envelopeId, mode || "live");
    }
  }, [envelopeId, finishAck, mode, session.docuSignAck]);

  useEffect(() => {
    if (!envelopeId || mode !== "live" || session.docuSignAck) return;
    pollRef.current = setInterval(() => {
      void fetchDocuSignEnvelopeStatus(envelopeId).then((status) => {
        if (status.status === "completed") {
          clearInterval(pollRef.current);
          void finishAck(envelopeId, "live");
        }
      });
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [envelopeId, finishAck, mode, session.docuSignAck]);

  async function startSigning() {
    setBusy(true);
    setError("");
    try {
      if (demoRecordingMode()) {
        const envelopeId = `sim-${session.fileCode}-${Date.now()}`;
        setEnvelopeId(envelopeId);
        setMode("simulated");
        setLimitation("Demo recording: simulated Buyer Funding Acknowledgment (not a legal signature).");
        return;
      }
      const returnUrl = `${window.location.origin}${window.location.pathname}?esign=complete`;
      const created = await createDocuSignEnvelope({
        fileCode: session.fileCode,
        returnUrl,
        instructionsViewedAt: viewedAt
      });
      setEnvelopeId(created.envelopeId);
      setMode(created.mode);
      setLimitation(created.limitation);
      if (created.signingUrl) {
        setSigningUrl(created.signingUrl);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not start DocuSign signing.");
    } finally {
      setBusy(false);
    }
  }

  async function simulateSign() {
    if (!envelopeId) return;
    setBusy(true);
    setError("");
    try {
      if (demoRecordingMode()) {
        const signedAt = new Date().toISOString();
        onSigned({
          mode: "simulated",
          envelopeId,
          instructionVersion: session.instructions.versionLabel,
          instructionVersionLabel: versionLabel,
          instructionHash: `demo-${session.fileCode}`,
          documentHash: `demo-doc-${session.fileCode}`,
          signedAt,
          signerName: session.buyerName,
          signerEmail: session.buyerEmail,
          limitation:
            limitation ||
            "Demo recording: simulated Buyer Funding Acknowledgment (not a legal signature)."
        });
        return;
      }
      await simulateDocuSignCompletion({
        fileCode: session.fileCode,
        envelopeId,
        instructionsViewedAt: viewedAt
      });
      await finishAck(envelopeId, "simulated");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Simulated signing failed.");
    } finally {
      setBusy(false);
    }
  }

  if (session.docuSignAck) {
    return (
      <section className="panel min-w-0 bg-[#e4efe9]" data-testid="docusign-ack-done">
        <p className="label">Buyer acknowledgment: Signed</p>
        <p className="mt-1 text-sm">
          Signed the Buyer Funding Acknowledgment for instruction version {versionLabel}.
        </p>
        <p className="mt-2 text-xs text-[#596158]">Envelope: {session.docuSignAck.envelopeId}</p>
      </section>
    );
  }

  return (
    <section className="panel min-w-0 overflow-hidden" data-testid="docusign-ack-panel">
      <p className="text-sm text-[#596158]">Instruction version {versionLabel}</p>
      {limitation ? <p className="notice mt-3 text-sm">{limitation}</p> : null}
      {error ? <p className="mt-3 text-sm text-[#8a2d10]">{error}</p> : null}

      {!envelopeId ? (
        <button
          className="button-primary mt-4"
          type="button"
          data-testid="docusign-start"
          disabled={busy}
          onClick={() => void startSigning()}
        >
          {busy ? "Preparing…" : "Continue to DocuSign"}
        </button>
      ) : null}

      {signingUrl ? (
        <div className="mt-4 -mx-4 min-w-0 sm:mx-0">
          <iframe
            title="DocuSign embedded signing"
            src={signingUrl}
            className="block h-[620px] w-full max-w-full border-y border-[#d8d2c4] bg-white sm:h-[520px] sm:rounded-md sm:border"
            data-testid="docusign-iframe"
          />
        </div>
      ) : null}

      {envelopeId && mode === "simulated" ? (
        <button
          className="button-secondary mt-4"
          type="button"
          data-testid="docusign-simulate-complete"
          disabled={busy}
          onClick={() => void simulateSign()}
        >
          {busy ? "Recording…" : "Complete simulated acknowledgment (dev)"}
        </button>
      ) : null}
    </section>
  );
}
